import { useEffect, useRef, useState } from "react";
import { AppLog } from "services";
import { useAppSelector } from "store/store";
import { RequestApi, RouteApi, StatusMsg } from "utils";

import { Camera, CircleStop, Grid2x2, Pencil, Plus, WalletCards } from "lucide-react";
import SearchBar from "components/app/search-bar/SearchBar";
import { Card } from "components/ui/card";
import CardMusicItemMusicLibraries from "pages/account-detail/music-libraries/CardMusicItemMusicLibraries";
import { AppRecord, AppRecordStatus } from "models/appRecord";
import InfiniteScroll from "react-infinite-scroll-component";
import UploadMusicAlbumPicture from "components/app/upload-profile/UploadMusicAlbumPicture";
import { logoFlipFlop } from "assets";
import BtnGoToTop from "components/app/btn-go-to-top/BtnGoToTop";
import DialogEditAndAddMusicAlbum from "./music-libraries/DialogEditAndAddMusicAlbum";
import DialogEditAndAddMusic from "./music-libraries/DialogEditAndAddMusic";
import AccountDetailLayout from "layout/account-detail-layout/AccountDetailLayout";
import SkeletonMusicLibrariesCard from "./music-libraries/skeleton/SkeletonMusicLibrariesCard";
import SkeletonMusicLibrariesAlbum from "./music-libraries/skeleton/SkeletonMusicLibrariesAlbum";
import SkeletonMusicLibrariesTable from "./music-libraries/skeleton/SkeletonMusicLibrariesTable";
import DialogMusicPlayer from "./music-libraries/DialogMusicPlayer";
import HoverCardDetail from "pages/hover-card-detail/HoverCardDetail";

export default function MusicLibraries() {
  const { token } = useAppSelector((state) => state.auth);
  const [view, setView] = useState(localStorage.getItem("sound-display-layout") || "card");

  const [hovered, setHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [musicDatas, setMusicDatas] = useState<any>(null);
  const [isInitSoundAlbumLoading, setIsInitSoundAlbumLoading] = useState<boolean>(false);
  const [isInitSoundLoading, setIsInitSoundLoading] = useState<boolean>(false);

  const [musicGenresDatas, setMusicGenresDatas] = useState<any[] | null>(null);
  const [musicAlbumDatas, setMusicAlbumDatas] = useState<any>(null);
  const [itemMusicAlbumClicked, setItemMusicAlbumClicked] = useState<any>(null);
  const [itemMusicClicked, setItemMusicClicked] = useState<any>(null);

  // Dialog================
  const [isDialogOpenEditAndAddMusicAlbum, setIsDialogOpenEditAndAddMusicAlbum] = useState<boolean>(false);
  const [isDialogOpenEditMusicAlbumState, setIsDialogOpenEditMusicAlbumState] = useState<boolean>(false);
  const [isDialogOpenEditMusicState, setIsDialogOpenEditMusicState] = useState<boolean>(false);
  const [isDialogOpenEditAndMusic, setIsDialogOpenEditAndMusic] = useState<boolean>(false);
  const [searchTextInput, setSearchTextInput] = useState<string>("");
  const [selectedNameMusicAlbums, setSelectedNameMusicAlbums] = useState<string>("");

  const changeView = (selectedView: string) => {
    setView(selectedView);
    localStorage.setItem("sound-display-layout", selectedView);
  };

  const [currentlyPlaying, setCurrentlyPlaying] = useState<string | null>(null);
  const [showLoadedMoreSongMessage, setShowLoadedMoreSongMessage] = useState(false);

  const seeMoreSongLoadingState = useRef<any>(null);
  const seeMoreAlbumLoadingState = useRef<any>(null);

  let seeMoreUserSoundState: AppRecord = {
    currentPage: 1,
    currentIndex: 0,
    nextPageUrl: "",
    totalRecord: 0,
    loadingStatus: AppRecordStatus.loading,
  };

  const fetchMoreUserSound = async ({ next }: { next: string }) => {
    seeMoreSongLoadingState.current = {
      ...seeMoreUserSoundState,
      loadingStatus: AppRecordStatus.loadingMore,
    };

    await RequestApi(next).then((responses) => {
      if (responses.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: responses["songs"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: responses["songs"]["next_page_url"] !== null ? AppRecordStatus.noLoadMore : AppRecordStatus.loaded,
        };

        const tmpPosts = [...musicDatas["songs"]["data"], ...responses["songs"]["data"]];

        const newPost = {
          ...musicDatas,
          songs: { ...musicDatas?.songs, data: tmpPosts },
        };
        setMusicDatas(newPost);
        seeMoreSongLoadingState.current = stateRecord;
      }
    });
  };

  const initUserSound = async () => {
    setIsInitSoundLoading(true);
    try {
      const url = `${RouteApi.getUserSoundLibrary}token=${token}`;
      const data = {
        search_text: searchTextInput,
        music_album_id: selectedNameMusicAlbums ?? "",
      };
      const response = await RequestApi(url, { data });
      if (response.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: response["songs"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: response["songs"]["next_page_url"] != null ? AppRecordStatus.loaded : AppRecordStatus.noLoadMore,
        };
        setMusicDatas(response);
        seeMoreSongLoadingState.current = stateRecord;
      }
    } catch (error) {
      AppLog.show({
        message: `[initUserSound]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setIsInitSoundLoading(false);
    }
  };

  const fetchMoreUserMusicAlbum = async ({ next }: { next: string }) => {
    if (next === "") return;
    seeMoreAlbumLoadingState.current = {
      ...seeMoreUserSoundState,
      loadingStatus: AppRecordStatus.loadingMore,
    };

    await RequestApi(next).then((responses) => {
      if (responses.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: responses["music_albums"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: responses["music_albums"]["next_page_url"] !== null ? AppRecordStatus.noLoadMore : AppRecordStatus.loaded,
        };

        setMusicAlbumDatas((prev: any) => ({
          ...prev,
          data: [...prev.data, ...responses.music_albums.data],
        }));
        seeMoreAlbumLoadingState.current = stateRecord;
      }
    });
  };

  const initUserMusicAlbum = async () => {
    setIsInitSoundAlbumLoading(true);
    try {
      const url = `${RouteApi.getUserSoundAlbum}token=${token}`;

      const response = await RequestApi(url);
      if (response.status === "success") {
        const stateRecord: AppRecord = {
          currentPage: 1,
          currentIndex: 0,
          nextPageUrl: response["music_albums"]["next_page_url"] ?? "",
          totalRecord: 0,
          loadingStatus: response["music_albums"]["next_page_url"] != null ? AppRecordStatus.loaded : AppRecordStatus.noLoadMore,
        };
        setMusicAlbumDatas(response.music_albums);
        setMusicGenresDatas(response.music_genres);
        seeMoreAlbumLoadingState.current = stateRecord;
      }
    } catch (error) {
      AppLog.show({
        message: `[initUserMusicAlbum]`,
        status: StatusMsg.error,
        error,
      });
    } finally {
      setIsInitSoundAlbumLoading(false);
    }
  };

  const handleClickCategory = (songItem: any) => {
    setCurrentlyPlaying(null);
    if (selectedNameMusicAlbums === songItem?.id) return;
    if (songItem) {
      setSelectedNameMusicAlbums(songItem?.id);
      setItemMusicAlbumClicked(songItem);
    } else {
      setSelectedNameMusicAlbums("");
    }
  };

  // console.log("musicAlbumDatas", musicAlbumDatas);

  const handleScroll = () => {
    const container = containerRef.current;
    if (!container) return;

    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;

    const nearEndThreshold = 300;
    const atNearEnd = scrollWidth - scrollLeft - clientWidth <= nearEndThreshold;

    if (atNearEnd) {
      fetchMoreUserMusicAlbum({
        next: seeMoreAlbumLoadingState.current.nextPageUrl,
      });
    }
  };

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }
    return () => {
      if (container) container.removeEventListener("scroll", handleScroll);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!view) {
      localStorage.setItem("sound-display-layout", "card");
    }
  }, [view]);

  useEffect(() => {
    initUserMusicAlbum();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (seeMoreSongLoadingState.current?.nextPageUrl === "") {
      const timer = setTimeout(() => {
        setShowLoadedMoreSongMessage(true);
      }, 500);
      return () => clearTimeout(timer); // Cleanup to avoid memory leaks
    } else {
      setShowLoadedMoreSongMessage(false);
    }
  }, [seeMoreSongLoadingState.current?.nextPageUrl]);

  useEffect(() => {
    if (searchTextInput === "" || selectedNameMusicAlbums === "trending") {
      initUserSound();
    } else {
      initUserSound();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTextInput, selectedNameMusicAlbums]);

  return (
    <AccountDetailLayout>
      <div className=" flex lg:flex-row md:flex-row flex-col justify-between left-0 items-center mb-3 w-full ">
        <div className="lg:w-auto md:w-auto w-full">
          <h1 className="font-semibold lg:text-lg md:text-md sm:text-sm">Music Libraries</h1>
          <p className="text-muted-foreground font-semibold lg:text-md md:text-sm text-[13px] whitespace-nowrap">A Digital Archive for Music Lovers and Creators</p>
        </div>
        <nav className="lg:w-auto md:w-auto w-full border-gray-200 pt-5 flex items-center justify-end lg:flex lg:items-center lg:justify-between md:flex md:items-center md:justify-between">
          <div className="flex gap-3 items-center">
            <>
              <HoverCardDetail
                title="Add New Music"
                description={
                  <>
                    <p className="mb-2 font-medium text-sm text-gray-800">Upload and configure new music tracks for scheduling or playback.</p>
                    <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                      <li>Add song metadata such as title, artist, and album.</li>
                      <li>Assign genres or moods for better organization.</li>
                      <li>Schedule playback times or link to events/playlists.</li>
                      <li>Ensure proper licensing and audio quality before publishing.</li>
                    </ul>
                  </>
                }
              >
                <button
                  onClick={() => {
                    setIsDialogOpenEditMusicState(false);
                    setIsDialogOpenEditAndMusic(true);
                  }}
                  className={`font-semibold rounded-full lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 flex items-center justify-center text-md border border-gray-300`}
                >
                  <Plus className="text-xl font-semibold" />
                </button>
              </HoverCardDetail>

              <button
                onClick={() => {
                  if (selectedNameMusicAlbums === "") {
                    return null;
                  } else {
                    setIsDialogOpenEditMusicAlbumState(true);
                    setIsDialogOpenEditAndAddMusicAlbum(true);
                  }
                }}
                className={`font-semibold rounded-full lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 flex items-center justify-center text-md border border-gray-300 
                    ${selectedNameMusicAlbums === "" ? "bg-gray-300 cursor-not-allowed dark:bg-accent" : "cursor-pointer"}`}
              >
                <Pencil className="font-semibold size-5" />
              </button>
            </>
            <label
              htmlFor="file-upload-edit"
              className="flex border border-gray-300 lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 justify-center rounded-full text-md cursor-pointer dark:bg-accent font-semibold items-center"
            >
              <CircleStop className=" size-5" />
            </label>
          </div>

          {/* Search & View Switch */}
          <div className="flex gap-2 items-center">
            <div className="flex-1 me-2 ms-4">
              <div className="relative">
                <SearchBar
                  onValueChange={(value) => {
                    setSearchTextInput(value);
                  }}
                />
              </div>
            </div>

            {/* View Toggle Button */}
            <HoverCardDetail
              title=""
              description={
                <>
                  <p className="mb-2 font-medium text-sm dark:text-white">Switch between different display modes to view your songs or items.</p>
                  <ul className="list-disc pl-5 text-sm dark:text-gray-300 space-y-1">
                    <li>
                      <strong>Card View:</strong> Displays items as individual cards with detailed info and visuals.
                    </li>
                    <li>
                      <strong>Table View:</strong> Shows items in a compact, sortable table for easy comparison and bulk actions.
                    </li>
                    <li>Use the toggle to choose the view that best fits your task or preference.</li>
                  </ul>
                </>
              }
            >
              <div className="relative">
                {view === "card" ? (
                  <button
                    onClick={() => changeView("table")}
                    className="flex border border-gray-300 lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 justify-center rounded-full text-md  cursor-pointer font-semibold items-center"
                  >
                    <Grid2x2 className=" size-5" />
                  </button>
                ) : (
                  <button
                    onClick={() => changeView("card")}
                    className="flex border border-gray-300 lg:w-10 lg:h-10 md:w-10 md:h-10 w-8 h-8 justify-center rounded-full text-md cursor-pointer font-semibold items-center"
                  >
                    <WalletCards className=" size-5" />
                  </button>
                )}
              </div>
            </HoverCardDetail>
          </div>
        </nav>
      </div>

      {/* Sound Album */}
      <div
        ref={containerRef}
        className={`flex overflow-x-auto space-x-2 no-scrollbar mt-2 pb-1 ${hovered ? "scrollbar-visible" : "scrollbar-custom"}`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div className="grid grid-cols-1 w-full">
          <div className="flex h-full">
            <HoverCardDetail
              title="Create New Music Album"
              description={
                <>
                  <p className="mb-2 font-medium text-sm text-gray-800">Create a new music album by grouping tracks under a common theme or artist.</p>
                  <ul className="list-disc pl-5 text-sm text-gray-700 space-y-1">
                    <li>Set album title, artwork, and release date.</li>
                    <li>Add existing or newly uploaded tracks to the album.</li>
                    <li>Define genre, mood, or label associations.</li>
                    <li>Organize albums for playlists, streaming, or promotional events.</li>
                  </ul>
                </>
              }
            >
              <Card
                onClick={() => {
                  setIsDialogOpenEditMusicAlbumState(false);
                  setIsDialogOpenEditAndAddMusicAlbum(true);
                }}
                className="flex h-full  justify-center rounded-md cursor-pointer items-center me-2 px-[22px]"
              >
                <Plus className=" w-6 h-6" />
              </Card>
            </HoverCardDetail>

            <Card
              onClick={() => {
                handleClickCategory(null);
              }}
              className={`w-[200px] h-16 rounded-md cursor-pointer font-semibold pe-[90px] me-2 ${
                selectedNameMusicAlbums === "" ? "bg-gray-200 dark:bg-accent dark:hover:bg-slate-900" : "dark:hover:bg-slate-900"
              }`}
            >
              <div className="flex h-full">
                <div className="">
                  {/* Image */}
                  <div className="flex h-full justify-center p-1 w-12 items-center">
                    <img src={logoFlipFlop} alt="logo" />
                  </div>
                </div>

                <div className="py-1">
                  <p className="text-md font-semibold px-2">Trending</p>
                </div>
              </div>
            </Card>

            {isInitSoundAlbumLoading && (
              <div className="flex space-x-2">
                {[...Array(7)].map((_: any, index: number) => {
                  return (
                    <div key={index}>
                      <SkeletonMusicLibrariesAlbum />
                    </div>
                  );
                })}
              </div>
            )}

            {musicAlbumDatas && (
              <div className="flex space-x-2">
                {musicAlbumDatas?.data.map((item: any) => {
                  return (
                    <Card
                      key={item.id}
                      onClick={() => {
                        handleClickCategory(item);
                      }}
                      className={`flex w-[200px] group h-16 rounded-md cursor-pointer font-semibold overflow-hidden ${
                        selectedNameMusicAlbums === item?.id ? "bg-gray-200 dark:bg-accent dark:hover:bg-slate-900" : "dark:hover:bg-slate-900"
                      }`}
                    >
                      <div className="relative">
                        <div className="flex h-full justify-center p-2 rounded-full w-[60px] items-center">
                          {item.thumbnail ? (
                            <img src={item.thumbnail} className="h-full rounded-full object-cover" alt="song" />
                          ) : (
                            <div className="flex h-full items-center">
                              <img src={logoFlipFlop} className="object-cover" alt="logo" />
                            </div>
                          )}
                        </div>

                        <div className="flex bg-black/50 justify-center rounded-bl-md rounded-tl-md text-white absolute group-hover:opacity-100 inset-0 items-center opacity-0 transition-opacity">
                          <UploadMusicAlbumPicture
                            itemAlbum={item}
                            onUploadSuccess={(valueSuccess: any) => {
                              setMusicAlbumDatas((prevState: any) => ({
                                ...prevState,
                                data: prevState?.data?.map((el: any) => (el.id === valueSuccess.id ? { ...el, ...valueSuccess } : el)),
                              }));
                            }}
                          />
                        </div>
                      </div>

                      <div className="font-semibold px-2 py-1">
                        <p className="text-md line-clamp-1">{item?.name}</p>
                        <p className="text-muted-foreground text-xs font-semibold line-clamp-2">{item?.sub_title}</p>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Skeleton Content Card and table */}
      {view === "card" ? (
        <>
          {isInitSoundLoading && (
            <div className="grid grid-cols-1 gap-2 gap-x-2 gap-y-3 lg:grid-cols-3 mb-16 md:grid-cols-2 min-h-[50px] mt-2">
              {[...Array(23)].map((_: any, index: number) => {
                return (
                  <div key={index}>
                    <SkeletonMusicLibrariesCard />
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>{isInitSoundLoading && <SkeletonMusicLibrariesTable />}</>
      )}

      {/* Music Player */}
      <div className="">
        {musicDatas ? (
          <InfiniteScroll
            className=""
            dataLength={musicDatas?.songs?.data?.length || 0}
            next={() =>
              fetchMoreUserSound({
                next: seeMoreSongLoadingState.current.nextPageUrl,
              })
            }
            hasMore={!!seeMoreSongLoadingState.current.nextPageUrl}
            loader={<div className="flex justify-center w-full absolute bottom-2 left-0"></div>}
            endMessage={
              <>
                {showLoadedMoreSongMessage && (
                  <p className="text-center w-full absolute bottom-0 left-0 pb-4">
                    <b>Nothing more to load!</b>
                  </p>
                )}
              </>
            }
          >
            {view === "card" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2 gap-x-2 gap-y-3  mb-16  min-h-[50px] mt-2">
                {musicDatas?.songs?.data?.map((item: any) => {
                  return (
                    <CardMusicItemMusicLibraries
                      setItemMusicClicked={(value) => {
                        setIsDialogOpenEditMusicState(true);
                        setItemMusicClicked(value);
                        setIsDialogOpenEditAndMusic(true);
                      }}
                      setMusicDatas={setMusicDatas}
                      key={item.id}
                      musicData={item}
                    />
                  );
                })}
              </div>
            ) : (
              <table className="border-collapse rounded-md font-sans mb-20 min-w-full mt-2 overflow-hidden">
                <thead className="bg-gray-200 w-full dark:bg-[#2c3a52] left-0 sticky top-0">
                  <tr className="border-none">
                    <th className="w-[100px] first:rounded-tl-md last:rounded-tr-md px-4 py-2">Image</th>
                    <th className="text-start w-2/6 px-4 py-2">Title</th>
                    <th className="w-1/6 px-4 py-2">Singer</th>
                    <th className="w-1/6 px-4 py-2">Author</th>
                    <th className="w-1/6 px-4 py-2">Genres</th>
                    <th className="w-1/6 first:rounded-tl-md last:rounded-tr-md px-4 py-2">Commercial Price</th>
                  </tr>
                </thead>
                <tbody>
                  {musicDatas?.songs?.data.map((item: any, index: number) => (
                    <tr
                      onClick={() => {
                        setIsDialogOpenEditMusicState(true);
                        setItemMusicClicked(item);
                        setIsDialogOpenEditAndMusic(true);
                      }}
                      key={index}
                      className={`text-center cursor-pointer font-medium ${
                        index % 2 === 0 ? "bg-gray-100 dark:bg-[#1e293b] " : "bg-gray-200 dark:bg-[#2c3a52] "
                      } hover:bg-gray-300`}
                    >
                      <td className="h-[100px] w-[100px] relative">
                        <button
                          onClick={(event) => {
                            event.stopPropagation(); // Prevents opening the edit dialog
                          }}
                          className="bg-slate-600 p-1 rounded-full -translate-x-1/2 -translate-y-1/2 absolute left-1/2 top-1/2"
                        >
                          {/* <UploadProductServicePhoto
                              editItemByCategories={item}
                              onUploadSuccess={({ res, name }) => {
                                
                              }}
                            /> */}
                          <Camera />
                        </button>
                        {item.thumbnail ? (
                          <img src={item.thumbnail} className="h-full rounded-bl-md rounded-tl-md w-[80px] object-cover" alt="song" />
                        ) : (
                          <div className="flex h-full w-[80px] items-center ms-1">
                            <img src={logoFlipFlop} className="object-cover" alt="logo" />
                          </div>
                        )}
                      </td>
                      <td className="text-start px-4 py-2">
                        <p className="text-md line-clamp-3">{item.sound_title}</p>
                      </td>
                      <td className="px-4 py-2">
                        <p className="line-clamp-3">{item.sound_singer}</p>
                      </td>
                      <td className="px-4 py-2">
                        <p className="line-clamp-3">{item.sound_author}</p>
                      </td>
                      <td className="px-4 py-2">
                        <p className="line-clamp-3">{item.sound_genres}</p>
                      </td>
                      <td className="text-destructive dark:text-white px-4 py-2">{item.commercial_price}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </InfiniteScroll>
        ) : (
          <></>
        )}
      </div>

      <BtnGoToTop />

      <DialogEditAndAddMusicAlbum
        isDialogOpen={isDialogOpenEditAndAddMusicAlbum}
        setIsDialogOpen={setIsDialogOpenEditAndAddMusicAlbum}
        musicAlbumItem={isDialogOpenEditMusicAlbumState ? itemMusicAlbumClicked : null}
        onUploadSuccess={({ res, name }) => {
          if (name === "delete") {
            setMusicAlbumDatas((prev: any) => {
              return {
                ...prev,
                data: (prev.data || []).filter((item: any) => item.id !== res.id),
              };
            });
          } else {
            setMusicAlbumDatas((prev: any) => {
              const updatedData = [...(prev.data || [])].map((item: any) => (item.id === res.id ? { ...item, ...res } : item));
              if (!updatedData.some((item) => item.id === res.id)) {
                updatedData.push(res);
              }
              return { ...prev, data: updatedData };
            });
          }
        }}
      />

      <DialogEditAndAddMusic
        musicAlbumDatas={musicAlbumDatas}
        musicGenresDatas={musicGenresDatas}
        itemMusicAlbumClicked={itemMusicAlbumClicked}
        isDialogOpen={isDialogOpenEditAndMusic}
        setIsDialogOpen={setIsDialogOpenEditAndMusic}
        musicItem={isDialogOpenEditMusicState ? itemMusicClicked : null}
        onUploadSuccess={({ res, name }) => {
          setMusicDatas((prev: any) => {
            if (!prev || !prev.songs) return prev; // Ensure prev and prev.songs exist

            if (name === "delete") {
              return {
                ...prev,
                songs: {
                  ...prev.songs,
                  data: (prev.songs.data || []).filter((item: any) => item.sound_id !== res.sound_id),
                },
              };
            } else {
              const updatedData = (() => {
                const existingIndex = (prev.songs.data || []).findIndex((item: any) => item.sound_id === res.sound_id);

                if (existingIndex !== -1) {
                  console.log("musicDatas existing", musicDatas);

                  // If found, update the existing item
                  return (prev.songs.data || []).map((item: any) => (item.sound_id === res.sound_id ? { ...item, ...res } : item));
                } else {
                  console.log("musicDatas notfound", musicDatas);
                  // If not found, add the new item
                  return [...(prev.songs.data || []), res];
                }
              })();

              return {
                ...prev,
                songs: {
                  ...prev.songs,
                  data: updatedData,
                },
              };
            }
          });
        }}
      />

      <DialogMusicPlayer />
    </AccountDetailLayout>
  );
}
